/**
 * The one status-change dialog (ADR 0006).
 *
 * It exists because there used to be two ways to move an order: this modal on
 * the detail page, which asked for a reason and offered the per-line restock
 * decisions, and a bare popover on the Orders list that posted an empty comment
 * and moved no stock. Both surfaces mount this now, so a cancellation from a
 * table row is the same transaction as one from the order page.
 *
 * Three rules live here, and each is enforced again on the server:
 *   - the exits (Cancelled / Returned / On hold) need a typed reason;
 *   - On hold needs a category, and remembers where it was held from;
 *   - cancelling or returning an order that has finished goods needs a
 *     Sellable/Damaged decision per line, or the stock silently disappears.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { Input, Modal, Radio, Select } from 'antd';
import {
  EXIT_STATUSES,
  requiresReason,
  restocksFrom,
  settableStatuses,
} from '../../utils/enums/OrderStatusEnum';
import { useStateContext } from '../../contexts/ContextProvider';
import { SHELL_THEME } from '../../shellTokens';
import { fmtBDT, statusMeta } from './orderPrimitives';

const HOLD = 11;
const RETURNED = 7;
const CANCELLED = 8;

const HOLD_REASONS = [
  { value: 'REPAIR', label: 'Repair' },
  { value: 'RESCHEDULE', label: 'Reschedule' },
  { value: 'OTHER', label: 'Other' },
];

/**
 * A line's one-line identity in the restock list. ProductResource sends the
 * type/fabric fields as raw ids, so the names are only there when a caller has
 * hydrated them — the description is the reliable human label, and the id is the
 * last resort. The old cancel modal read `.name` off the ids and rendered " · · ".
 */
const lineLabel = (p) => {
  const named = [p.productType?.name, p.fabrics?.name, p.fabricType?.name]
    .filter(Boolean)
    .join(' · ');
  return named || p.description || `Line ${p.id}`;
};

export default function StatusChangeModal({
  open,
  order,
  permissions,
  initialStatus = null,
  onCancel,
  onSubmit,
  submitting = false,
}) {
  const { darkMode } = useStateContext();
  // A Modal portals to <body>, outside the element that publishes the shell's
  // CSS variables — var(--border) there resolves to nothing and silently drops
  // the whole border declaration. Hex, taken from the same palette.
  const t = SHELL_THEME[darkMode ? 'dark' : 'light'];

  const [status, setStatus] = useState(initialStatus);
  const [comment, setComment] = useState('');
  const [holdReason, setHoldReason] = useState('REPAIR');
  const [conditions, setConditions] = useState({});

  // Reopening for a different order, or from a different trigger, starts clean —
  // a reason typed for one order must never ride along to the next.
  useEffect(() => {
    if (!open) return;
    setStatus(initialStatus ?? order?.status ?? null);
    setComment('');
    setHoldReason('REPAIR');
    setConditions({});
  }, [open, initialStatus, order?.id, order?.status]);

  const options = useMemo(() => {
    const settable = settableStatuses(permissions);
    const current = statusMeta(order?.status);
    // The order's own status is shown so the select is never blank, but it can't
    // be submitted — the server rejects a no-op change anyway.
    const list = settable.some((s) => s.value === order?.status)
      ? settable
      : [current, ...settable];
    return list.map((s) => ({ value: s.value, label: s.label }));
  }, [permissions, order?.status]);

  const products = order?.products || [];
  const needsReason = requiresReason(status);
  const needsLines =
    [CANCELLED, RETURNED].includes(status) && restocksFrom(order?.status);
  const reasonGiven = comment.trim().length > 0;
  const linesGiven = !needsLines || products.every((p) => conditions[p.id]);

  const heldFrom = order?.heldFromStatus ?? null;
  const isRelease = order?.status === HOLD && status !== HOLD;

  // What is still missing, said once next to the button that is waiting for it,
  // rather than as a colour on every control it applies to.
  const blocker = (() => {
    if (!status || status === order?.status) return 'Choose a different status.';
    if (needsReason && !reasonGiven) return 'A reason is required for this status.';
    if (!linesGiven) {
      const left = products.filter((p) => !conditions[p.id]).length;
      return `Mark ${left} more line${left === 1 ? '' : 's'} Sellable or Damaged.`;
    }
    return null;
  })();

  const submit = () => {
    onSubmit({
      newStatus: status,
      statusComment: comment.trim(),
      holdReason: status === HOLD ? holdReason : undefined,
      lines: needsLines
        ? products.map((p) => ({ productId: p.id, condition: conditions[p.id] }))
        : undefined,
    });
  };

  return (
    <Modal
      title={isRelease ? 'Release order from hold' : 'Update order status'}
      open={open}
      onOk={submit}
      onCancel={onCancel}
      okText={isRelease ? 'Release' : 'Update status'}
      okButtonProps={{
        disabled: blocker !== null,
        loading: submitting,
        danger: EXIT_STATUSES.includes(status),
      }}
      destroyOnClose
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginTop: 8 }}>
        {order?.status === HOLD && heldFrom && (
          <div style={{ font: '400 12.5px Outfit, sans-serif', color: t.muted }}>
            Held from <strong>{statusMeta(heldFrom).label}</strong> — releasing sends it back there.
          </div>
        )}

        <Select
          value={status}
          onChange={setStatus}
          options={options}
          style={{ width: '100%' }}
          placeholder="Choose a status"
        />

        {status === HOLD && (
          <div>
            <div style={{ font: '400 12.5px Outfit, sans-serif', color: t.muted, marginBottom: 6 }}>
              What is it being held for?
            </div>
            <Radio.Group
              value={holdReason}
              onChange={(e) => setHoldReason(e.target.value)}
              options={HOLD_REASONS}
              optionType="button"
              buttonStyle="solid"
            />
          </div>
        )}

        {/* No warning tint on an untouched field: the placeholder says it is
            required and the disabled button holds the door. Ringing every empty
            control amber on open reads as "you did something wrong" before the
            operator has done anything at all. */}
        <Input.TextArea
          rows={3}
          placeholder={needsReason ? 'Reason (required)' : 'Comment (optional)'}
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          maxLength={200}
          showCount={needsReason}
        />

        {needsLines && (
          <div style={{ border: `1px solid ${t.border}`, borderRadius: 10, padding: 12 }}>
            <div
              style={{
                font: '400 12.5px Outfit, sans-serif',
                color: t.muted,
                marginBottom: 8,
              }}
            >
              This order has finished goods. Mark each line Sellable to add it to
              ready stock, or Damaged to write it off.
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {products.map((p) => (
                <div
                  key={p.id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 12,
                  }}
                >
                  <span style={{ font: '400 12.5px Outfit, sans-serif', minWidth: 0 }}>
                    {lineLabel(p)}
                    <span style={{ color: t.dim }}>
                      {' '}· Qty {p.unit} · {fmtBDT(p.price)}
                    </span>
                  </span>
                  <Select
                    style={{ width: 150, flex: 'none' }}
                    placeholder="Choose"
                    value={conditions[p.id]}
                    onChange={(v) => setConditions((prev) => ({ ...prev, [p.id]: v }))}
                    options={[
                      { value: 'SELLABLE', label: 'Sellable' },
                      { value: 'DAMAGED', label: 'Damaged' },
                    ]}
                  />
                </div>
              ))}
            </div>
          </div>
        )}

        {/* What is still missing, said once, in plain type — instead of an amber
            ring on every control it applies to. antd 5.4.2 has no footer render
            prop, so it sits at the foot of the body, above the buttons. */}
        {blocker && (
          <div style={{ font: '400 12px Outfit, sans-serif', color: t.dim }}>{blocker}</div>
        )}
      </div>
    </Modal>
  );
}
